import { useState, useRef, useEffect } from 'react'
import { reportError } from 'utils/error'

interface UseVideoPlaybackProps {
  videoDuration: number
  videoUrl: string
}

interface UseVideoPlaybackReturn {
  videoRef: React.RefObject<HTMLVideoElement>
  currentTime: number
  isPlaying: boolean
  handlePlayPause: () => Promise<void>
  handleStop: () => void
  handleTimeChange: (time: number) => void
  setIsPlaying: (isPlaying: boolean) => void
}

export const useVideoPlayback = ({ videoDuration, videoUrl }: UseVideoPlaybackProps): UseVideoPlaybackReturn => {
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const animationFrameRef = useRef<number>()

  // Handle time updates during playback
  useEffect(() => {
    if (!isPlaying) return

    const updateTime = () => {
      if (videoRef.current) {
        const newTime = videoRef.current.currentTime
        setCurrentTime(newTime)

        // Stop playback if we reach the end of the video
        if (newTime >= videoDuration) {
          handleStop()
          return
        }
      }
      animationFrameRef.current = requestAnimationFrame(updateTime)
    }

    animationFrameRef.current = requestAnimationFrame(updateTime)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying, videoDuration])

  const handleStop = () => {
    setIsPlaying(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
    setCurrentTime(0)
  }

  const handlePlayPause = async () => {
    if (!videoRef.current) return

    if (isPlaying) {
      // Pause
      setIsPlaying(false)
      videoRef.current.pause()
    } else {
      // Play
      setIsPlaying(true)
      try {
        await videoRef.current.play()
      } catch (error) {
        reportError(error)
        setIsPlaying(false)
      }
    }
  }

  const handleTimeChange = (time: number) => {
    setCurrentTime(time)
    if (videoRef.current) {
      videoRef.current.currentTime = time
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  return {
    videoRef,
    currentTime,
    isPlaying,
    handlePlayPause,
    handleStop,
    handleTimeChange,
    setIsPlaying,
  }
}
